import { ApiClient } from '@/api/client';
import { UserJsonldUserReadUserPrivateRead } from '@/api/generated';
import { useQuery } from '@tanstack/react-query';
import useApi from 'hooks/useApi';
import JsCookie from 'js-cookie';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import formatQueryParams from 'utils/cms/formatQueryParams';
import { sha256 } from 'utils/crypto';
import { cmsFactory } from 'utils/factories/cms';
import { COOKIE_CONTEXT_HASH } from 'utils/ssr/cookies';
import { defaultLang } from '../../i18n/constants';
import { DtoPage } from '../../types/cms';
import { CMSPages } from './types';
import { isInAppWebview } from 'utils/webview';

export const WALLET_2FA_PARAMETERS_URL = '/manage-account/connection/2fa-parameters';
export const WALLET_BALANCE_URL = '/manage-account/my-wallet/my-balance';

const formatAcfDto = ({ acf }: { acf: CMSPages['acf'] }): DtoPage['acf'] => {
  return {
    ...acf,
    eventType: acf.eventType || null,
    background: {
      image: acf.background?.image || null,
      position: acf.background?.position || null,
    },
    tooltip: acf.tooltip || null,
    displayTitle: acf.display_title,
    displayText: acf.display_text || null,
    ctas: acf.ctas ? acf.ctas : null,
    foreground: acf.foreground ? acf.foreground : null,
    displayMenuTitle: acf?.display_menu_title || null,
    footer: acf.footer,
    footerMenus: {
      ...(acf.footer_menus?.footer_classic && {
        footerClassic: {
          menuItems: acf.footer_menus.footer_classic.menu_items,
          discord: {
            title: acf.footer_menus.footer_classic.acf.discord_title || null,
            link: acf.footer_menus.footer_classic.acf.discord_link || null,
            icon: acf.footer_menus.footer_classic.acf.discord_icon || null,
          },
          retailer: {
            image: acf.footer_menus.footer_classic.acf.retailer_image || null,
            link: acf.footer_menus.footer_classic.acf.retailer_link || null,
          },
        },
      }),
      ...(acf.footer_menus?.footer_legal_classic && {
        footerLegalClassic: {
          menuItems: acf.footer_menus.footer_legal_classic.menu_items,
        },
      }),
    },
    header: acf.header,
    headerMenus: {
      ...(acf.header_menus?.header_classic && {
        headerClassic: {
          menuItems: acf.header_menus?.header_classic.menu_items,
        },
      }),
      ...(acf.header_menus?.header_account_classic && {
        headerAccountClassic: {
          menuItems: acf.header_menus?.header_account_classic.menu_items,
          footerImage: acf.header_menus?.header_account_classic.acf.image_footer_menu || null,
        },
      }),
      ...(acf.header_menus?.header_editorial_classic && {
        headerEditorialClassic: {
          menuItems: acf.header_menus?.header_editorial_classic.menu_items,
        },
      }),
    },
    linkedInternalMenu: acf.linked_internal_menu || null,
    ...(acf.widgets && { widgets: acf.widgets }),
    layout: acf.layout,
    redirectToFirstChild: acf?.redirect_to_first_child || null,
    parentForceUrl: acf?.parent_force_url || null,
    noParentBack: acf?.no_parent_back || null,
    forceQueries: acf?.force_queries || null,
    ...(!!acf.query_parameters && { queryParameters: formatQueryParams(acf.query_parameters) }),
    ...(!!acf.form_options && {
      formOptions: {
        formName: acf.form_options.form_name,
        containerName: acf.form_options.container_name,
        beforeForm: acf.form_options.before_form,
        afterForm: acf.form_options.after_form,
        submitLabel: acf.form_options.submit_label,
        useSteps: !!acf.form_options.use_steps,
      },
    }),
    ...(!!acf.empty_options && {
      emptyOptions: {
        title: acf.empty_options.title,
        image: acf.empty_options.image,
        text: acf.empty_options.text,
      },
    }),
    ...(!!acf.initial_options && {
      initialOptions: {
        title: acf.initial_options.title,
        image: acf.initial_options.image,
        text: acf.initial_options.text,
      },
    }),
    // add new fields here
    defaultBanner: acf?.default_banner || null,
    defaultMainIcon: acf?.default_main_icon || null,
    defaultDescription: acf?.default_description || null,
    defaultDescriptionActivatedPass: acf?.default_description_activated_pass || null,
    defaultSubDescription: acf?.default_sub_description || null,
    defaultLeagues: acf?.default_leagues || null,
    factionLeagues: acf?.faction_leagues || null,
    modalTitle: acf?.modal_title || null,
    modalDescription: acf?.modal_description || null,
    modalSubtitle: acf?.modal_subtitle || null,
    modalLink: acf?.modal_link || null,
  };
};

const formatPageDto = ({
  id,
  date,
  date_gmt: dateGmt,
  modified,
  slug,
  status,
  type,
  title,
  author,
  parent,
  menu_order: menuOrder,
  template,
  acf,
  siblings,
  yoast_head_json: yoastHeadJson,
  children,
  previous_child: previousChild,
  next_child: nextChild,
  url,
  wpUser,
  article_details: articleDetails,
  articles_categories: articlesCategories,
  articles,
  articles_total_items: articlesTotalItems,
  articles_items_per_page: articlesItemsPerPage,
  articles_total_pages: articlesTotalPages,
  factions,
  alterators,
  alterator_details: alteratorDetails,
  faction_details: factionDetails,
  general_settings: generalSettings,
  cousins,
}: CMSPages): DtoPage => {
  return {
    id,
    date,
    dateGmt,
    modified,
    slug,
    status,
    type,
    title,
    author,
    parent,
    template,
    acf: formatAcfDto({ acf }),
    articles: articles || null,
    articlesTotalItems: articlesTotalItems || null,
    articlesItemsPerPage: articlesItemsPerPage || null,
    articlesTotalPages: articlesTotalPages || null,
    factions: factions || null,
    generalSettings: {
      ...generalSettings,
      rgpdDisclaimer: generalSettings.rgpd_disclaimer || null,
      rgpdLink: generalSettings.rgpd_link || null,
      linkToShop: !!generalSettings.link_to_shop,
      cardLanguageSwitch: !!generalSettings.card_language_switch,
      defaultOgImage: generalSettings.default_og_image || '',
      deckBuilding: !!generalSettings.deck_building,
      marketplace: !!generalSettings.marketplace,
      printOnDemand: !!generalSettings.print_on_demand,
      playGame: !!generalSettings.play_game,
      defaultDescription: generalSettings.default_description || '',
      countries: generalSettings.countries || null,
      languages: generalSettings.languages.map((language) => {
        return {
          locale: language.language_code,
          reference: language.language_reference,
          name: language.language_label,
        };
      }),
      zendeskMessengerId: generalSettings.zendesk_messenger_id || null,
      seasonPassCurrentSeason: generalSettings.season_pass_current_season || null,
      salesFeesSimulation: generalSettings.sales_fees_simulation || null,
    },
    alterators: alterators || null,
    factionDetails: factionDetails || null,
    alteratorDetails: alteratorDetails || null,
    articleDetails: articleDetails || null,
    articlesCategories: articlesCategories || null,
    siblings,
    cousins:
      cousins?.map(({ is_current: isCurrent, ...cousin }) => {
        return { isCurrent, ...cousin };
      }) || null,
    yoastHeadJson,
    children,
    previousChild,
    nextChild,
    url,
    menuOrder,
    wpUser,
  };
};

export const uniformizeCmsUrl = (url: string) =>
  url
    .replace(/\/cards\/market\/[A-Za-z0-9]{26}/, '/cards/market/01XXXXXXXXXXXXXXXXXXXXXXXX')
    .replace(/\/cards\/(?!market)[^/]+/, '/cards/X')
    .replace(/\/scan\/[^/]+/, '/scan/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx')
    .replace(/\/card-exchange\/[^/]+/, '/card-exchange/x')
    .replace(/\/profile\/my-exchanges\/[A-Za-z0-9]{26}/, '/profile/my-exchanges/01XXXXXXXXXXXXXXXXXXXXXXXX')
    .replace(/\/profile\/[^/]+_\d{4}/, '/profile/X_0001')
    .replace(/\/decks\/[A-Za-z0-9]{26}/, '/decks/01XXXXXXXXXXXXXXXXXXXXXXXX')
    .replace(/\/events\/[A-Za-z0-9]{26}/, '/events/01XXXXXXXXXXXXXXXXXXXXXXXX')
    .replace(/\/shop-profile\/[A-Za-z0-9]{26}/, '/shop-profile/01XXXXXXXXXXXXXXXXXXXXXXXX');

type FetchSlugParams = Parameters<ApiClient['cms']['apiCmspagesGet']>[0];

export const fetchSlug = async (
  apiClient: ApiClient,
  { url, isMobileApp, locale: language, userContextHash, page, inSale }: FetchSlugParams,
) =>
  apiClient.cms
    .apiCmspagesGet({ url, locale: language || defaultLang, userContextHash, page, inSale, isMobileApp })
    // @ts-expect-error TODO the API DTO is not synced with data returned from Wordpress
    // see api/src/Cms/ApiResource/Pages.php
    .then((data) => formatPageDto(data.data as CMSPages));

export const useCmsData = (): DtoPage => {
  const { query } = useRouter();
  const { slug } = query;
  const isInApp = isInAppWebview();

  const { data } = useQuery<DtoPage>({
    queryKey: cmsFactory.page(slug),
    enabled: false,
  });

  if (!data) {
    console.error('no data found for current route ');
  }

  const dataSuperCharged = {
    ...data,
    generalSettings: {
      ...data?.generalSettings,
      marketplace: !!data?.generalSettings.marketplace && !isInApp,
    },
  };

  return (dataSuperCharged as DtoPage) || ({ acf: {}, parent: {} } as DtoPage);
};

const fetchModals = async (
  apiClient: ApiClient,
  url: string,
  language?: string,
  userContextHash?: string,
  page?: string,
) =>
  apiClient.cms
    .apiCmsmodalsGet({ url, locale: language || defaultLang, userContextHash, page })
    // @ts-expect-error TODO the API DTO is not synced with data returned from Wordpress
    .then((data) => formatAcfDto(data.data as CMSPages.acf));

export const useCMSmodals = ({ cmsModalName }: { cmsModalName: string }) => {
  const { locale } = useRouter();
  const apiClient = useApi();
  const session = useSession();

  const queryResult = useQuery({
    queryKey: cmsFactory.modal([cmsModalName, { locale }, session?.data?.userId || 'no-auth']),
    queryFn: () => fetchModals(apiClient, cmsModalName, locale, JsCookie.get(COOKIE_CONTEXT_HASH)),
  });

  return { queryResult };
};

export const generateContextHash = async (user: UserJsonldUserReadUserPrivateRead): Promise<string> =>
  sha256(JSON.stringify([user.roles?.sort(), user.betaTester, user.walletActivated]));

// In some cases, we need to clear context hash because we change a value used to build it
// IE wallet activation
export const clearContextHash = async () => {
  JsCookie.remove(COOKIE_CONTEXT_HASH);
};

// Used to update context hash, IE when updating role to user
export const checkContextHash = async (user: UserJsonldUserReadUserPrivateRead) => {
  const contextHash = JsCookie.get(COOKIE_CONTEXT_HASH);
  const newContextHash = await generateContextHash(user);

  if (contextHash && contextHash !== newContextHash) {
    JsCookie.set(COOKIE_CONTEXT_HASH, newContextHash);
  }
};
